import { THEMES, type CustomTheme, type Theme } from '@moussenaires/shared';
import { useTranslation } from 'react-i18next';

import './ThemePicker.css';

const THEME_LABEL_KEY: Record<Theme, string> = {
  light: 'settings.themeLight',
  dark: 'settings.themeDark',
  alliance: 'settings.themeAlliance',
  horde: 'settings.themeHorde',
  void: 'settings.themeVoid',
  poison: 'settings.themePoison',
  custom: 'settings.themeCustom',
};

const CUSTOM_COLOR_FIELDS: { key: keyof CustomTheme; labelKey: string; fallback: string }[] = [
  { key: 'bg', labelKey: 'settings.colorBg', fallback: '#0c0e14' },
  { key: 'surface', labelKey: 'settings.colorSurface', fallback: '#171a24' },
  { key: 'text', labelKey: 'settings.colorText', fallback: '#eef0f6' },
  { key: 'accent', labelKey: 'settings.colorAccent', fallback: '#8b7bff' },
  { key: 'accentContrast', labelKey: 'settings.colorAccentContrast', fallback: '#0c0e14' },
];

/**
 * The light/dark/faction/void/poison/custom theme grid, used both for a
 * member's own appearance (`SettingsModal`) and for the admin-configured
 * site default (`AdminPage`) — same picker, different destination for the
 * choice.
 */
export function ThemePicker({
  theme,
  customTheme,
  onSelectTheme,
  onCustomColorChange,
}: {
  theme: Theme;
  customTheme: CustomTheme;
  onSelectTheme: (theme: Theme) => void;
  onCustomColorChange: (key: keyof CustomTheme, value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <div className="theme-grid">
        {THEMES.map((option) => (
          <button
            key={option}
            type="button"
            data-theme={option}
            className={`theme-swatch ${theme === option ? 'theme-swatch-active' : ''}`}
            onClick={() => onSelectTheme(option)}
          >
            <span className="theme-swatch-preview" />
            {t(THEME_LABEL_KEY[option])}
          </button>
        ))}
      </div>

      {theme === 'custom' && (
        <div className="custom-theme-fields">
          <h4>{t('settings.customColors')}</h4>
          {CUSTOM_COLOR_FIELDS.map(({ key, labelKey, fallback }) => (
            <label key={key} className="color-field">
              <span>{t(labelKey)}</span>
              <input
                type="color"
                value={customTheme[key] ?? fallback}
                onChange={(event) => onCustomColorChange(key, event.target.value)}
              />
            </label>
          ))}
        </div>
      )}
    </>
  );
}
